import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import { Card, Row, Col, Button, Progress, Spin } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { ZoomStatus } from 'types';
import {
  ZoomDateRangeFilter,
  ZoomRangePresets,
  ZoomStatusBadge,
} from 'molecules';
import { ZoomDashboardData } from 'core/hooks';
import { ZoomAccountUsageFieldsFragment } from 'core/graphql';
import { formatBytes } from 'core/utils/format';
import { formatWarsaw } from 'core/utils/zoomTime';

interface IDashboardActionsLoading {
  refreshAll: boolean;
  sync: boolean;
  rematch: boolean;
  report: boolean;
}

export interface IDashboardProps {
  range: [DateTime, DateTime];
  onSetRange: (from: DateTime, to: DateTime) => void;
  onQuickYesterdayToday: () => void;
  data?: ZoomDashboardData;
  loading: boolean;
  usage?: ZoomAccountUsageFieldsFragment;
  usageLoading: boolean;
  tooShortCount: number;
  actionsLoading: IDashboardActionsLoading;
  onRefreshAll: () => void;
  onSync: () => void;
  onRematch: () => void;
  onSendReport: () => void;
  onGoMissing: () => void;
  onGoOrphan: () => void;
  onGoFailed: () => void;
  onGoTooShort: () => void;
}

type PipelineCounts = NonNullable<ZoomDashboardData['pipeline']>;

const PIPELINE_ROWS: Array<{
  key: keyof PipelineCounts;
  status: ZoomStatus;
}> = [
  { key: 'downloading', status: ZoomStatus.DOWNLOADING },
  { key: 'uploaded', status: ZoomStatus.UPLOADED },
  { key: 'aiProcessing', status: ZoomStatus.AI_PROCESSING },
  { key: 'aiCompleted', status: ZoomStatus.AI_COMPLETED },
  { key: 'published', status: ZoomStatus.PUBLISHED },
  { key: 'failed', status: ZoomStatus.FAILED },
];

/** Progress-bar colour by storage fill level. */
const usageColor = (percent: number): string => {
  if (percent > 85) {
    return '#ef4444';
  }
  if (percent >= 70) {
    return '#f59e0b';
  }
  return '#10b981';
};

/** Zoom dashboard — storage, pipeline counters, «refresh all» and follow-ups. */
export const Dashboard = ({
  range,
  onSetRange,
  onQuickYesterdayToday,
  data,
  loading,
  usage,
  usageLoading,
  tooShortCount,
  actionsLoading,
  onRefreshAll,
  onSync,
  onRematch,
  onSendReport,
  onGoMissing,
  onGoOrphan,
  onGoFailed,
  onGoTooShort,
}: IDashboardProps) => {
  const { t } = useTranslation();

  const pipeline = data?.pipeline;
  const missing = data?.lessonsMissing ?? 0;
  const orphan = data?.recordingsOrphan ?? 0;
  const failed = pipeline?.failed ?? 0;
  const percent = Math.round(usage?.storageUsedPercent ?? 0);

  return (
    <Wrapper>
      <Header>
        <Title>{t('pages.zoom.dashboard.title')}</Title>
        <HeaderRight>
          <ZoomDateRangeFilter
            value={range}
            onChange={(next) => {
              if (next) {
                onSetRange(next[0], next[1]);
              }
            }}
          />
          <ZoomRangePresets value={range} onChange={onSetRange} />
          <Button onClick={onQuickYesterdayToday}>
            {t('pages.zoom.dashboard.quickYesterdayToday')}
          </Button>
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            loading={actionsLoading.refreshAll}
            onClick={onRefreshAll}
          >
            {t('pages.zoom.dashboard.refreshAll')}
          </Button>
        </HeaderRight>
      </Header>

      {/* Block B — Zoom storage */}
      <Card
        size="small"
        title={t('pages.zoom.dashboard.storage.title')}
        style={{ marginBottom: 16 }}
      >
        <Spin spinning={usageLoading && !usage}>
          {usage ? (
            <>
              {usage.planUsageAvailable ? (
                <>
                  <Progress
                    percent={percent}
                    strokeColor={usageColor(percent)}
                  />
                  <StorageLine>
                    {t('pages.zoom.dashboard.storage.used')}:{' '}
                    <b>{formatBytes(usage.planStorageUsedBytes)}</b>
                    {' / '}
                    {formatBytes(usage.planStorageTotalBytes)}
                    {' · '}
                    {t('pages.zoom.dashboard.storage.free')}:{' '}
                    <b>{formatBytes(usage.planStorageFreeBytes)}</b>
                  </StorageLine>
                </>
              ) : (
                <QuotaUnavailable>
                  <b>{t('pages.zoom.dashboard.storage.quotaUnavailable')}</b>
                  <QuotaHint>
                    {t('pages.zoom.dashboard.storage.quotaHint')}
                  </QuotaHint>
                </QuotaUnavailable>
              )}
              <UsageGrid>
                <UsageStat
                  label={t('pages.zoom.dashboard.storage.users')}
                  value={usage.usersCount}
                />
                <UsageStat
                  label={t('pages.zoom.dashboard.storage.recordings')}
                  value={usage.recordingsCount}
                />
                <UsageStat
                  label={t('pages.zoom.dashboard.storage.files')}
                  value={usage.filesCount}
                />
                <UsageStat
                  label={t('pages.zoom.dashboard.storage.ourRecordings')}
                  value={formatBytes(usage.recordingsSizeBytes)}
                />
              </UsageGrid>
              <SyncedAt>
                {t('pages.zoom.dashboard.storage.syncedAt')}:{' '}
                {formatWarsaw(usage.syncedAt)}
              </SyncedAt>
            </>
          ) : (
            <Muted>{t('pages.zoom.dashboard.storage.noData')}</Muted>
          )}
        </Spin>
      </Card>

      {/* Block C — pipeline of the selected day */}
      <Spin spinning={loading}>
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Card size="small" title={t('pages.zoom.dashboard.lessons.title')}>
              <Metric label={t('pages.zoom.dashboard.lessons.total')}>
                {data?.lessonsTotal ?? 0}
              </Metric>
              <Metric label={t('pages.zoom.dashboard.lessons.matched')}>
                {data?.lessonsMatched ?? 0}
              </Metric>
              <ClickableMetric
                label={t('pages.zoom.dashboard.lessons.missing')}
                onClick={onGoMissing}
              >
                {missing}
              </ClickableMetric>
              <Metric label={t('pages.zoom.dashboard.lessons.skipped')}>
                {data?.lessonsSkipped ?? 0}
              </Metric>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card
              size="small"
              title={t('pages.zoom.dashboard.recordings.title')}
            >
              <Metric label={t('pages.zoom.dashboard.recordings.total')}>
                {data?.recordingsTotal ?? 0}
              </Metric>
              <ClickableMetric
                label={t('pages.zoom.dashboard.recordings.orphan')}
                onClick={onGoOrphan}
              >
                {orphan}
              </ClickableMetric>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card size="small" title={t('pages.zoom.dashboard.pipeline.title')}>
              {PIPELINE_ROWS.map((row) => (
                <PipelineRow key={row.key}>
                  <ZoomStatusBadge status={row.status} animated={false} />
                  <Count>{pipeline?.[row.key] ?? 0}</Count>
                </PipelineRow>
              ))}
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* Block D — needs attention */}
      <Card
        size="small"
        title={t('pages.zoom.dashboard.needsAttention')}
        style={{ marginTop: 16 }}
      >
        <AttentionRow
          label={t('pages.zoom.dashboard.attention.noRecording')}
          count={missing}
          onClick={onGoMissing}
        />
        <AttentionRow
          label={t('pages.zoom.dashboard.attention.failed')}
          count={failed}
          onClick={onGoFailed}
        />
        <AttentionRow
          label={t('pages.zoom.dashboard.attention.orphan')}
          count={orphan}
          onClick={onGoOrphan}
        />
        <AttentionRow
          label={t('pages.zoom.dashboard.attention.tooShort')}
          count={tooShortCount}
          onClick={onGoTooShort}
        />
      </Card>

      <Actions>
        <Button loading={actionsLoading.sync} onClick={onSync}>
          {t('pages.zoom.dashboard.actions.sync')}
        </Button>
        <Button loading={actionsLoading.rematch} onClick={onRematch}>
          {t('pages.zoom.dashboard.actions.rematch')}
        </Button>
        <Button loading={actionsLoading.report} onClick={onSendReport}>
          {t('pages.zoom.dashboard.actions.sendReport')}
        </Button>
      </Actions>
    </Wrapper>
  );
};

interface IMetricProps {
  label: string;
  children: React.ReactNode;
}

const Metric = ({ label, children }: IMetricProps) => (
  <MetricRow>
    <span>{label}</span>
    <Count>{children}</Count>
  </MetricRow>
);

const ClickableMetric = ({
  label,
  children,
  onClick,
}: IMetricProps & { onClick: () => void }) => (
  <MetricRow as="button" type="button" onClick={onClick} clickable>
    <span>{label}</span>
    <Count>{children}</Count>
  </MetricRow>
);

const UsageStat = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <UsageStatBox>
    <UsageStatValue>{value}</UsageStatValue>
    <UsageStatLabel>{label}</UsageStatLabel>
  </UsageStatBox>
);

const AttentionRow = ({
  label,
  count,
  onClick,
}: {
  label: string;
  count: number;
  onClick: () => void;
}) => (
  <AttentionItem type="button" onClick={onClick}>
    <span>{label}</span>
    <AttentionCount active={count > 0}>{count}</AttentionCount>
  </AttentionItem>
);

const Wrapper = styled.div`
  width: 100%;
  padding: 16px 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const StorageLine = styled.div`
  font-size: 14px;
  color: #374151;
  margin-top: 4px;
`;

const QuotaUnavailable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 8px;
`;

const QuotaHint = styled.span`
  font-size: 12px;
  color: #9ca3af;
`;

const UsageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 10px;
  margin-top: 14px;
`;

const UsageStatBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
`;

const UsageStatValue = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const UsageStatLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const SyncedAt = styled.div`
  margin-top: 10px;
  font-size: 12px;
  color: #9ca3af;
`;

const MetricRow = styled.div<{ clickable?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 6px 0;
  border: none;
  background: transparent;
  font-size: 14px;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  color: ${({ clickable }) => (clickable ? '#2563eb' : 'inherit')};

  & + & {
    border-top: 1px solid #f0f0f0;
  }
`;

const PipelineRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
`;

const Count = styled.span`
  font-weight: 600;
`;

const AttentionItem = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  border: none;
  background: transparent;
  font-size: 14px;
  color: #2563eb;
  cursor: pointer;

  & + & {
    border-top: 1px solid #f0f0f0;
  }
`;

const AttentionCount = styled.span<{ active: boolean }>`
  min-width: 28px;
  text-align: center;
  padding: 1px 8px;
  border-radius: 10px;
  font-weight: 600;
  color: ${({ active }) => (active ? '#b91c1c' : '#6b7280')};
  background: ${({ active }) => (active ? '#fee2e2' : '#f1f5f9')};
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin: 20px 0;
  flex-wrap: wrap;
`;

const Muted = styled.div`
  color: #9ca3af;
  padding: 12px 0;
`;
